/**
 * batch-68 controle (FO-342 §3.6 + §11) — het TVT-plafond per emmer.
 *
 * PO-besluit: het maximale saldo per TVT-emmer (TVT werk en TVT reis apart) is
 * `VerlofBeleid.tvtWerkurenMaxWeken` resp. `tvtReisurenMaxWeken` keer één
 * werkweek. Een werkweek telt **38 uur zonder ATV-opbouw en 40 uur met
 * ATV-opbouw**; bij parttime (minder dan 38 contracturen) de contracturen per
 * week.
 *
 * In FO-342 wordt het plafond alleen GESIGNALEERD (regel 15 en de afkap van de
 * TVT-normverwachting), niet afgedwongen: `triggerTVTFromWeekstaat` boekt
 * ongelimiteerd. Afdwingen in de opbouw is een eigen FO (FO-342 §11) en hoort
 * dan deze helper te hergebruiken.
 *
 * Eén bron (batch-68 integratie, ADDENDA gedeelde bestanden 8): de
 * plafondsignalering van de verloningscontrole (FO-342 regel 15) en de tekst
 * "maximum sparen" van de werktijd-uitleg (FO-344, `maxSparenPerPot`) rekenen
 * allebei met deze functie, zodat het scherm van de medewerker en de controle
 * van payroll nooit een ander maximum noemen.
 *
 * "Met ATV-opbouw" = ATV bouwt echt op (`berekenAtvUrenPerWeek` > 0: 38 uur
 * contract met een werkweek van 40). Geef daarom de werkuren van de
 * contractmutatie mee; een ATV-vlag zonder opbouw (werkuren gepind op 38)
 * telt dan als zonder ATV. Zonder werkuren geldt de vlag zelf.
 */

import { berekenAtvUrenPerWeek } from './verlof-profiel'

/** Een volle werkweek zonder ATV-opbouw (CAO Metaal en Techniek). */
export const TVT_PLAFOND_WEEKUREN_ZONDER_ATV = 38
/** Een volle werkweek met ATV-opbouw (38 + 2 ATV-uren). */
export const TVT_PLAFOND_WEEKUREN_MET_ATV = 40

export interface TvtPlafondWeekInput {
  /** Contracturen per week van de contractmutatie (bij parttime < 38). */
  contractUrenPerWeek: number
  /** ATV-vlag op de contractmutatie. */
  atvEnabled: boolean
  /**
   * `werkUrenPerWeek` van de contractmutatie (null = afgeleid). Gegeven: alleen
   * echte ATV-opbouw telt als "met ATV". Weggelaten: de vlag zelf.
   */
  werkUrenPerWeek?: number | null
}

export interface TvtPlafondInput extends TvtPlafondWeekInput {
  /** `VerlofBeleid.tvtWerkurenMaxWeken` of `tvtReisurenMaxWeken` (default 1). */
  maxWeken: number
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * De uren van één werkweek voor het plafond: 38 zonder ATV, 40 met ATV, en bij
 * parttime (contracturen tussen 0 en 38) de contracturen zelf. Zonder bruikbare
 * contracturen valt hij terug op de volle week (38/40) zodat een ontbrekend
 * contract nooit een plafond van 0 oplevert.
 */
export function tvtPlafondWeekUren(input: TvtPlafondWeekInput): number {
  const contract = Number(input.contractUrenPerWeek)
  if (Number.isFinite(contract) && contract > 0 && contract < TVT_PLAFOND_WEEKUREN_ZONDER_ATV) {
    return round2(contract)
  }
  const metAtv =
    input.werkUrenPerWeek === undefined
      ? input.atvEnabled
      : berekenAtvUrenPerWeek(contract, input.werkUrenPerWeek, input.atvEnabled) > 0
  return metAtv ? TVT_PLAFOND_WEEKUREN_MET_ATV : TVT_PLAFOND_WEEKUREN_ZONDER_ATV
}

/**
 * Het maximale saldo van één TVT-emmer: `maxWeken × werkweek-uren`, op twee
 * decimalen. Een negatief of ongeldig aantal weken telt als 0 (geen ruimte).
 *
 *   tvtPlafond({ maxWeken: 1, contractUrenPerWeek: 38, atvEnabled: false }) -> 38
 *   tvtPlafond({ maxWeken: 1, contractUrenPerWeek: 38, atvEnabled: true })  -> 40
 *   tvtPlafond({ maxWeken: 1, contractUrenPerWeek: 24, atvEnabled: false }) -> 24
 */
export function tvtPlafond(input: TvtPlafondInput): number {
  const weken = Number(input.maxWeken)
  if (!Number.isFinite(weken) || weken <= 0) return 0
  return round2(weken * tvtPlafondWeekUren(input))
}
